import React, { useState, useEffect } from 'react';
import './LicenseRequest.css';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID, LICENSE_API_URL } from '../config';
import { formatDate as fmtDate } from '../utils/dateFormat';

const AVAILABLE_FEATURES = ['DTP', 'DXA_REPORTING'];

const LicenseRequest = ({ repo, isAdmin, onLicenseGenerated, disabled = false }) => {
  const [requesting, setRequesting] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [features, setFeatures] = useState([]);
  const [maxXrays, setMaxXrays] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentLicense, setCurrentLicense] = useState(null);
  // Self-reported usage from the site (Mixpanel LICENSE_VALIDATION via LicenseUsageSync)
  const [siteUsage, setSiteUsage] = useState(null);
  const [loadingLicense, setLoadingLicense] = useState(true);
  const [updatingYaml, setUpdatingYaml] = useState(false);
  const [maxXraysError, setMaxXraysError] = useState('');
  const [deletingLicense, setDeletingLicense] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const licenseApiUrl = LICENSE_API_URL;
  const gitopsApiUrl = process.env.REACT_APP_GITOPS_API_URL || API_URL;

  // Check for existing license on mount
  useEffect(() => {
    checkExistingLicense();
    fetchSiteUsage();
  }, [repo]);

  // Set default dates and maxXrays when component mounts
  useEffect(() => {
    const today = new Date();
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(today.getFullYear() + 1);

    setStartDate(today.toISOString().split('T')[0]);
    setEndDate(oneYearFromNow.toISOString().split('T')[0]);

    // Set default maxXrays: 10,000 for distributors, 0 (unlimited) for admins
    setMaxXrays(isAdmin ? 0 : 10000);
  }, [isAdmin]);

  const fetchSiteUsage = async () => {
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const res = await fetch(`${gitopsApiUrl}/license-usage?repo=${encodeURIComponent(repo)}`, {
        headers: { Authorization: idToken }
      });
      if (res.ok) {
        const data = await res.json();
        setSiteUsage(data.usage ? { ...data.usage, synced_at: data.synced_at } : null);
      }
    } catch (err) {
      console.error('Error fetching site usage:', err);
    }
  };

  const checkExistingLicense = async () => {
    setLoadingLicense(true);
    try {
      // Get current session for authentication
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      
      // First, try to get the current rho.yaml to see if it has a license
      const yamlResponse = await fetch(`${gitopsApiUrl}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: { Authorization: idToken }
      });
      
      if (yamlResponse.ok) {
        const yamlText = await yamlResponse.text();
        const yaml = require('js-yaml');
        const doc = yaml.load(yamlText);
        const existingLicense = doc?.spec?.source?.helm?.valuesObject?.license;
        
        console.log('License check - existingLicense:', existingLicense);
        console.log('License check - type:', typeof existingLicense);
        console.log('License check - length:', existingLicense ? existingLicense.length : 'N/A');
        
        // Check if license exists and is not empty (empty string means no license)
        if (existingLicense && existingLicense.trim() !== '') {
          console.log('License found, validating...');
          // Validate the existing license
          const validateResponse = await fetch(`${licenseApiUrl}/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ license: existingLicense })
          });
          
          if (validateResponse.ok) {
            const validateData = await validateResponse.json();
            console.log('License validation response:', validateData);
            if (validateData.valid) {
              setCurrentLicense(validateData);
              setLicenseKey(existingLicense);
              console.log('License loaded successfully');
            } else {
              console.log('License validation failed:', validateData);
            }
          } else {
            console.log('License validation request failed:', validateResponse.status);
          }
        } else {
          console.log('No valid license found, showing license generation form');
        }
      }
    } catch (err) {
      console.error('Error checking existing license:', err);
    } finally {
      setLoadingLicense(false);
    }
  };

  // Helper function to get current session (copied from RepoConfig)
  const getCurrentSession = () => {
    return new Promise((resolve, reject) => {
      const { CognitoUserPool } = require('amazon-cognito-identity-js');
      const userPool = new CognitoUserPool({
        UserPoolId: USER_POOL_ID,
        ClientId: USER_POOL_CLIENT_ID,
      });
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) return reject('No user');
      cognitoUser.getSession((err, session) => {
        if (err) return reject(err);
        resolve(session);
      });
    });
  };

  const handleFeatureToggle = (feature) => {
    setFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const handleMaxXraysChange = (value) => {
    const numValue = Number(value);
    
    if (!isAdmin) {
      // Distributor validation: 0 < Gross Annual X-rays <= 500,000
      if (numValue === 0) {
        setMaxXraysError('Gross Annual X-rays must be greater than 0 for distributors');
        return;
      } else if (numValue > 500000) {
        setMaxXraysError('Gross Annual X-rays cannot exceed 500,000 for distributors');
        return;
      } else {
        setMaxXraysError('');
      }
    }
    
    setMaxXrays(value);
  };

  const updateRhoYaml = async (newLicense) => {
    setUpdatingYaml(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      
      // Get current rho.yaml
      const yamlResponse = await fetch(`${gitopsApiUrl}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: { Authorization: idToken }
      });
      
      if (!yamlResponse.ok) {
        throw new Error('Failed to fetch current rho.yaml');
      }
      
      const yamlText = await yamlResponse.text();
      const yaml = require('js-yaml');
      const doc = yaml.load(yamlText);
      
      // Update the license field
      if (!doc.spec) doc.spec = {};
      if (!doc.spec.source) doc.spec.source = {};
      if (!doc.spec.source.helm) doc.spec.source.helm = {};
      if (!doc.spec.source.helm.valuesObject) doc.spec.source.helm.valuesObject = {};
      
      doc.spec.source.helm.valuesObject.license = newLicense;
      
      // Update the file in GitHub
      const updateResponse = await fetch(`${gitopsApiUrl}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: { 
          Authorization: idToken,
          'Content-Type': 'text/yaml'
        },
        body: yaml.dump(doc)
      });
      
      if (!updateResponse.ok) {
        throw new Error('Failed to update rho.yaml in GitHub');
      }
      
      console.log('Successfully updated rho.yaml with new license');
    } catch (err) {
      console.error('Error updating rho.yaml:', err);
      throw err;
    } finally {
      setUpdatingYaml(false);
    }
  };

  const removeLicenseFromYaml = async () => {
    setUpdatingYaml(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      
      // Get current rho.yaml
      const yamlResponse = await fetch(`${gitopsApiUrl}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: { Authorization: idToken }
      });
      
      if (!yamlResponse.ok) {
        throw new Error('Failed to fetch current rho.yaml');
      }
      
      const yamlText = await yamlResponse.text();
      const yaml = require('js-yaml');
      const doc = yaml.load(yamlText);
      
      // Set license to empty string instead of removing the field
      if (doc.spec?.source?.helm?.valuesObject) {
        doc.spec.source.helm.valuesObject.license = '';
        console.log('Set license to empty string in rho.yaml');
      }
      
      // Update the file in GitHub
      const updateResponse = await fetch(`${gitopsApiUrl}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: { 
          Authorization: idToken,
          'Content-Type': 'text/yaml'
        },
        body: yaml.dump(doc)
      });
      
      if (!updateResponse.ok) {
        throw new Error('Failed to update rho.yaml in GitHub');
      }
      
      console.log('Successfully updated rho.yaml with empty license');
    } catch (err) {
      console.error('Error removing license from rho.yaml:', err);
      throw err;
    } finally {
      setUpdatingYaml(false);
    }
  };

  const deleteLicense = async () => {
    if (!window.confirm('Are you sure you want to delete this license? This action cannot be undone.')) {
      return;
    }

    setDeletingLicense(true);
    setError(null);
    try {
      console.log('Deleting license...');
      // Remove license from rho.yaml
      await removeLicenseFromYaml();
      
      console.log('License removed from rho.yaml, clearing local state...');
      // Clear local state
      setCurrentLicense(null);
      setLicenseKey('');
      setDeleteSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => setDeleteSuccess(false), 3000);
      
      if (onLicenseGenerated) {
        console.log('Calling onLicenseGenerated with empty string');
        onLicenseGenerated(''); // Clear the license
      }
      
      console.log('License deletion completed successfully');
    } catch (err) {
      console.error('License deletion failed:', err);
      setError('Failed to delete license: ' + err.message);
    } finally {
      setDeletingLicense(false);
    }
  };

  const generateLicense = async () => {
    setRequesting(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate Gross Annual X-rays for distributors
      if (!isAdmin) {
        const numXrays = Number(maxXrays);
        if (numXrays === 0) {
          throw new Error('Gross Annual X-rays must be greater than 0 for distributors');
        } else if (numXrays > 500000) {
          throw new Error('Gross Annual X-rays cannot exceed 500,000 for distributors');
        }
      }

      let finalStartDate = startDate;
      let finalEndDate = endDate;

      // Validate dates
      if (!finalStartDate || !finalEndDate) {
        throw new Error('Start date and end date are required');
      }

      const start = new Date(finalStartDate);
      const end = new Date(finalEndDate);

      if (end < start) {
        throw new Error('End date must be greater than or equal to start date');
      }

      // Distributors must select an end date between 1 week and 1 year from start
      if (!isAdmin) {
        const minEnd = new Date(start);
        minEnd.setDate(minEnd.getDate() + 7);
        if (end < minEnd) {
          throw new Error('License duration must be at least 1 week');
        }
        const maxEnd = new Date(start);
        maxEnd.setFullYear(maxEnd.getFullYear() + 1);
        if (end > maxEnd) {
          throw new Error('License duration cannot exceed 1 year');
        }
      }

      // Prepare license request payload
      const licenseRequest = {
        repo,
        issued_to: repo, // or allow user to enter a name
        features,
        max_xrays: Number(maxXrays),
        start_date: finalStartDate,
        end_date: finalEndDate
      };

      // Get current session for authentication
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      
      // Call backend /licenses endpoint
      const response = await fetch(`${licenseApiUrl}/licenses`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(licenseRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate license');
      }

      const data = await response.json();
      setLicenseKey(data.license);
      setCurrentLicense(data);
      setSuccess(true);
      
      // Update rho.yaml in GitHub
      await updateRhoYaml(data.license);
      
      if (onLicenseGenerated) {
        onLicenseGenerated(data.license);
      }
    } catch (err) {
      setError('Failed to generate license: ' + err.message);
    } finally {
      setRequesting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(licenseKey);
    setSuccess(false); // Reset success message
  };

  const formatDate = (timestamp) => fmtDate(timestamp);

  // The terms the issuer specified: start, end, volume, features. Rendered the
  // same way for admins, distributors, and the post-generation summary so the
  // user can always see what the current license actually says.
  const renderLicenseTerms = (lic) => {
    if (!lic) return null;
    const unlimited = lic.max_xrays === 0 || lic.max_xrays === undefined || lic.max_xrays === null;
    return (
      <div className="license-details">
        <p><strong>Issued To:</strong> {lic.issued_to}</p>
        <p><strong>Features:</strong> {formatFeatures(lic.features)}</p>
        <p><strong>License Start:</strong> {formatDate(lic.issued_at) || '—'}</p>
        <p><strong>License End:</strong> {formatDate(lic.expires_at) || '—'}</p>
        <p><strong>Gross Annual X-rays:</strong> {unlimited ? 'Unlimited' : Number(lic.max_xrays).toLocaleString()}</p>
        {!unlimited && (
          <p><strong>X-rays Used:</strong> {Number(lic.xrays_used || 0).toLocaleString()}</p>
        )}
        <p><strong>Status:</strong> {lic.status || 'active'}</p>
        {renderSiteUsage(lic)}
      </div>
    );
  };

  // What the site itself last reported (daily LICENSE_VALIDATION event).
  const renderSiteUsage = (lic) => {
    if (!siteUsage || siteUsage.xrays_used === null || siteUsage.xrays_used === undefined) return null;
    const unlimited = !siteUsage.max_xrays;
    const pct = unlimited ? null : Math.min(100, (siteUsage.xrays_used / siteUsage.max_xrays) * 100);
    const day = (v) => { const d = new Date(v); return isNaN(d) ? null : d.toISOString().slice(0, 10); };
    const differs = lic && lic.expires_at && siteUsage.license_expires_at && day(lic.expires_at) !== day(siteUsage.license_expires_at);
    const color = pct === null ? 'var(--text-secondary)' : pct >= 90 ? 'var(--status-none-fg)' : pct >= 75 ? 'var(--status-expired-fg)' : 'var(--status-valid-fg)';
    return (
      <div style={{ marginTop: '10px', padding: '10px 12px', border: '1px solid var(--border, #e1e5e9)', borderRadius: '8px' }}>
        <p style={{ margin: 0 }}>
          <strong>Reported by site:</strong>{' '}
          {Number(siteUsage.xrays_used).toLocaleString()} {unlimited ? 'x-rays used (unlimited licence)' : `of ${Number(siteUsage.max_xrays).toLocaleString()} x-rays used`}
          {pct !== null && <span style={{ marginLeft: '8px', color, fontWeight: 600 }}>{pct.toFixed(1)}%</span>}
          {siteUsage.valid === false && <span style={{ marginLeft: '8px', color: 'var(--status-none-fg)', fontWeight: 700 }}>site reports licence invalid ({siteUsage.reason || 'unknown'})</span>}
        </p>
        {pct !== null && (
          <div style={{ height: '6px', background: 'var(--surface-3, #eee)', borderRadius: '3px', marginTop: '6px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color }} />
          </div>
        )}
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Last report {siteUsage.reported_at ? formatDate(siteUsage.reported_at) : 'unknown'}
          {siteUsage.license_expires_at ? `, site licence ends ${formatDate(siteUsage.license_expires_at)}` : ''}
          {differs ? '. The site is running a different licence than the one shown above; it may not have picked up the latest rho.yaml yet.' : ''}
        </p>
      </div>
    );
  };

  const formatFeatures = (features) => {
    return features && features.length > 0 ? features.join(', ') : 'None';
  };

  // Show loading state
  if (loadingLicense) {
    return (
      <div className="license-request">
        <h3>License Management</h3>
        <div>Loading license information...</div>
      </div>
    );
  }

  // Show existing license for distributors with management options
  if (currentLicense && !isAdmin) {
    return (
      <div className="license-request">
        <h3>License Management</h3>
        
        {/* Success message for delete */}
        {deleteSuccess && (
          <div style={{
            color: 'var(--status-valid-fg)',
            background: 'var(--status-valid-bg)',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(141, 240, 193, 0.22)',
            fontSize: '14px'
          }}>
            License deleted successfully!
          </div>
        )}

        {/* Current License Display */}
        <div className="license-info" style={{ marginBottom: '20px' }}>
          <h4>Current License</h4>
          {renderLicenseTerms(currentLicense)}
          
          {/* License Key Display */}
          <div className="license-key-container" style={{ marginTop: '15px' }}>
            <label>License Key:</label>
            <div className="license-key-display">
              <input 
                type="text" 
                value={licenseKey} 
                readOnly 
                className="license-key-input"
              />
              <button 
                className="btn btn-secondary copy-btn"
                onClick={copyToClipboard}
              >
                Copy
              </button>
            </div>
          </div>

          {/* License Management Actions */}
          <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid var(--border)' }}>
            <h5 style={{
              margin: '0 0 12px 0',
              color: 'var(--text-secondary)',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}>License Actions</h5>

            {isAdmin ? (
              // Admin: Can generate new licenses and delete existing ones
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    setCurrentLicense(null);
                    setLicenseKey('');
                    setFeatures([]);
                    setMaxXrays(isAdmin ? 0 : 10000);
                  }}
                  style={{
                    background: 'var(--color-bit-blue)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  Generate New License
                </button>
                <button
                  onClick={deleteLicense}
                  disabled={disabled || deletingLicense || updatingYaml}
                  style={{
                    background: 'var(--status-none-fg)',
                    color: '#3a0e1a',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: (disabled || deletingLicense || updatingYaml) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 700,
                    opacity: deletingLicense || updatingYaml ? 0.6 : 1
                  }}
                >
                  {deletingLicense ? 'Deleting…' : updatingYaml ? 'Updating…' : 'Delete License'}
                </button>
              </div>
            ) : (
              // Distributor: Can only view license, no actions allowed
              <div style={{
                padding: '12px 14px',
                background: 'var(--surface-3)',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                textAlign: 'center'
              }}>
                <strong style={{ color: 'var(--text-primary)' }}>License Management Restricted</strong><br/>
                As a distributor, you can only generate one license per repository.
                This license cannot be deleted or replaced.
              </div>
            )}

            {error && (
              <div style={{
                color: 'var(--status-none-fg)',
                fontSize: '12px',
                marginTop: '8px',
                fontWeight: 500
              }}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show license request form (for admins or when no license exists)
  return (
    <div className="license-request">
      <h3>License Management</h3>
      
      {currentLicense && isAdmin && (
        <div className="license-info" style={{ marginBottom: '20px' }}>
          <h4>Current License</h4>
          {renderLicenseTerms(currentLicense)}
          <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '10px' }}>
            As an admin, you can request a new license to add features or extend the license.
          </p>
        </div>
      )}
      
      {!licenseKey ? (
        <div className="license-request-form">
          <p>
            {currentLicense ? (
              <>Request a new license for <strong>{repo}</strong> (replacing existing license).</>
            ) : (
              <>Generate a license for <strong>{repo}</strong>.</>
            )}
            {isAdmin ? ' As an admin, you can generate licenses for any repository.' : ' As a distributor, you can generate licenses for repositories you own.'}
            {!isAdmin && currentLicense && (
              <span style={{ color: '#e74c3c', fontWeight: '500' }}>
                {' '}Note: Distributors can only generate one license per repository.
              </span>
            )}
          </p>
          <div className="license-info">
            <h4>License Details:</h4>
            <ul>
              <li><strong>Duration:</strong> {isAdmin ? '1 year (configurable)' : '1 week to 1 year'}</li>
              {isAdmin && (
                <li><strong>Features:</strong> 
                  <span>
                    {AVAILABLE_FEATURES.map(feature => (
                      <label key={feature} style={{ marginRight: '15px', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={features.includes(feature)}
                          onChange={() => handleFeatureToggle(feature)}
                        />{' '}
                        {feature.replace('_', ' ')}
                      </label>
                    ))}
                  </span>
                </li>
              )}
              {!isAdmin && (
                <li><strong>Features:</strong> Basic license (no premium features)</li>
              )}
              <li><strong>Gross Annual X-rays:</strong> 
                <input
                  type="number"
                  min={isAdmin ? "0" : "1"}
                  max={isAdmin ? "" : "500000"}
                  value={maxXrays}
                  onChange={e => handleMaxXraysChange(e.target.value)}
                  style={{ 
                    width: '80px', 
                    marginLeft: '8px',
                    borderColor: maxXraysError ? '#e74c3c' : undefined
                  }}
                  title={isAdmin ? "0 means unlimited" : "Must be between 1 and 500,000 for distributors"}
                />
                <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                  {isAdmin ? "(0 = unlimited)" : "(1-500,000 for distributors)"}
                </span>
                {maxXraysError && (
                  <div style={{
                    color: '#e74c3c',
                    fontSize: '12px',
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    {maxXraysError}
                  </div>
                )}
              </li>
              <li><strong>License Start Date:</strong> 
                {isAdmin ? (
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    style={{ marginLeft: '8px' }}
                  />
                ) : (
                  <span style={{ marginLeft: '8px', color: '#6c757d', fontStyle: 'italic' }}>
                    {startDate} (fixed)
                  </span>
                )}
              </li>
              <li><strong>License End Date:</strong>
                {isAdmin ? (
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    style={{ marginLeft: '8px' }}
                  />
                ) : (
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    min={(() => { const d = new Date(startDate); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0]; })()}
                    max={(() => { const d = new Date(startDate); d.setFullYear(d.getFullYear() + 1); return d.toISOString().split('T')[0]; })()}
                    style={{ marginLeft: '8px' }}
                  />
                )}
              </li>
              <li><strong>Customer ID:</strong> {repo}</li>
            </ul>
          </div>
          <button
            className="btn btn-primary"
            onClick={generateLicense}
            disabled={disabled || requesting || updatingYaml || maxXraysError || (!isAdmin && currentLicense)}
            style={{
              opacity: (disabled || (!isAdmin && currentLicense)) ? 0.6 : 1,
              cursor: (disabled || (!isAdmin && currentLicense)) ? 'not-allowed' : 'pointer'
            }}
          >
            {requesting ? 'Generating License...' : updatingYaml ? 'Updating GitHub...' : (currentLicense ? 'Request New License' : 'Generate License')}
          </button>
          {!isAdmin && currentLicense && (
            <div style={{
              color: '#e74c3c',
              fontSize: '12px',
              marginTop: '8px',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              <strong>License Generation Restricted:</strong> Distributors can only generate one license per repository.
            </div>
          )}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>
      ) : (
        <div className="license-result">
          <h4>License Generated Successfully!</h4>
          <div className="license-info" style={{ marginBottom: '15px' }}>
            {renderLicenseTerms(currentLicense)}
          </div>
          <div className="license-key-container">
            <label>License Key:</label>
            <div className="license-key-display">
              <input 
                type="text" 
                value={licenseKey} 
                readOnly 
                className="license-key-input"
              />
              <button 
                className="btn btn-secondary copy-btn"
                onClick={copyToClipboard}
              >
                Copy
              </button>
            </div>
          </div>
          <div className="license-actions">
            {isAdmin ? (
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setLicenseKey('');
                  setSuccess(false);
                }}
              >
                Generate New License
              </button>
            ) : (
              <div style={{
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #dee2e6',
                color: '#6c757d',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                <strong>License Generation Restricted</strong><br/>
                As a distributor, you can only generate one license per repository.
              </div>
            )}
          </div>
          {success && (
            <div className="success-message">
              License key copied to clipboard!
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LicenseRequest; 